<template>
  <basic-dialog :show="show" max-width="900px" @close="onCancel">
    <template v-slot:title>{{ title }}</template>

    <template v-slot:actions>
      <v-btn
        color="primary"
        :loading="saving"
        :disabled="!isValid"
        @click="onSave"
        >Save</v-btn
      >
    </template>

    <v-row v-if="loadingLive" align="center" class="px-3 pb-2">
      <v-progress-circular indeterminate size="18" width="2" class="mr-2" />
      <span class="caption text--secondary">Loading live pricing…</span>
    </v-row>

    <v-row align="center">
      <v-col cols="12" sm="6">
        <rate-type-list-select
          v-model="form.rateTypeCode"
          @onSelect="onRateTypeSelect"
          :outlined="outlinedInputs"
          hideNotInUse
          hide-details
          dense
          class="mb-0"
        ></rate-type-list-select>
      </v-col>
      <v-col cols="12" sm="6">
        <v-checkbox
          v-model="form.customPrice"
          label="Custom Price"
          hide-details
          dense
          class="mr-4 mt-0 pt-0"
        ></v-checkbox>
        <v-btn
          v-if="form.customPrice"
          text
          small
          color="primary"
          :disabled="loadingLive"
          @click="reloadLive"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Reload Live
        </v-btn>
      </v-col>
    </v-row>

    <h5 class="title">Buy</h5>
    <v-row>
      <v-col>
        <v-text-field
          :disabled="isPerPerson"
          label="Item"
          type="number"
          v-model.number="form.itemBuy"
          :outlined="outlinedInputs"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          :disabled="!isPerPerson"
          label="Adult"
          type="number"
          v-model.number="form.adultBuy"
          :outlined="outlinedInputs"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          :disabled="!isPerPerson"
          label="Child"
          type="number"
          v-model.number="form.childBuy"
          :outlined="outlinedInputs"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          :disabled="!isPerPerson"
          label="Infant"
          type="number"
          v-model.number="form.infantBuy"
          :outlined="outlinedInputs"
        ></v-text-field>
      </v-col>
      <v-col>
        <currency-list-select
          v-model="form.currencyBuy"
          :include-exchange-rate="true"
          :outlined="outlinedInputs"
        ></currency-list-select>
      </v-col>
    </v-row>

    <h5 class="title">Sell</h5>
    <v-row>
      <v-col>
        <v-text-field
          :disabled="isPerPerson"
          label="Item"
          type="number"
          v-model.number="form.itemSell"
          :outlined="outlinedInputs"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          :disabled="!isPerPerson"
          label="Adult"
          type="number"
          v-model.number="form.adultSell"
          :outlined="outlinedInputs"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          :disabled="!isPerPerson"
          label="Child"
          type="number"
          v-model.number="form.childSell"
          :outlined="outlinedInputs"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          :disabled="!isPerPerson"
          label="Infant"
          type="number"
          v-model.number="form.infantSell"
          :outlined="outlinedInputs"
        ></v-text-field>
      </v-col>
      <v-col>
        <currency-list-select
          v-model="form.currencySell"
          :include-exchange-rate="true"
          :outlined="outlinedInputs"
          hide-details
        ></currency-list-select>
      </v-col>
    </v-row>

    <v-alert v-if="liveError" type="warning" dense outlined class="mt-2 mb-0">{{
      liveError
    }}</v-alert>
  </basic-dialog>
</template>

<script>
import moment from "moment";
import BasicDialog from "../../shared/BasicDialog";
import RateTypeListSelect from "../../shared/RateTypeListSelect";
import CurrencyListSelect from "../../shared/CurrencyListSelect";
import { GET_PRICE_BREAKDOWN_QUERY } from "../../../../constants/graphql";
import { UPDATE_OPEN_GROUP_DEPARTURE_ITEM } from "../departure-gql";

export default {
  name: "OpenGroupDepartureItemPricingDialog",

  components: { BasicDialog, RateTypeListSelect, CurrencyListSelect },

  props: {
    show: { type: Boolean, default: false },
    item: { type: Object, default: null },
    departureStartDate: { type: String, default: null },
    department: { type: String, default: null },
  },

  data() {
    return {
      saving: false,
      loadingLive: false,
      liveError: "",
      rateType: null,
      form: this.blankForm(),
    };
  },

  computed: {
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    title() {
      if (!this.item) return "Pricing";
      const svc = this.item.service && this.item.service.name;
      const sup = this.item.supplier && this.item.supplier.name;
      return [sup, svc].filter(Boolean).join(" — ") || "Pricing";
    },
    isPerPerson() {
      return this.rateType ? this.rateType.includePax : true;
    },
    isValid() {
      return (
        this.form.rateTypeCode &&
        this.form.currencyBuy &&
        this.form.currencySell
      );
    },
    serviceStartDate() {
      if (!this.departureStartDate || !this.item) return null;
      const day = Number(this.item.day || 1) || 1;
      return moment(this.departureStartDate)
        .add(day - 1, "days")
        .format("YYYY-MM-DD");
    },
    serviceEndDate() {
      if (!this.serviceStartDate || !this.item) return null;
      const dur = Number(this.item.duration || 1) || 1;
      return moment(this.serviceStartDate)
        .add(dur, "days")
        .format("YYYY-MM-DD");
    },
  },

  watch: {
    show(open) {
      if (!open) return;
      this.loadFromItem();
      if (!this.form.customPrice) this.fetchLive();
    },
  },

  methods: {
    blankForm() {
      return {
        rateTypeCode: "",
        itemBuy: 0,
        adultBuy: 0,
        childBuy: 0,
        infantBuy: 0,
        itemSell: 0,
        adultSell: 0,
        childSell: 0,
        infantSell: 0,
        currencyBuy: "",
        currencySell: "",
        customPrice: false,
      };
    },
    loadFromItem() {
      this.liveError = "";
      const i = this.item || {};
      this.form = {
        rateTypeCode: i.rateTypeCode || "",
        itemBuy: i.itemBuy || 0,
        adultBuy: i.adultBuy || 0,
        childBuy: i.childBuy || 0,
        infantBuy: i.infantBuy || 0,
        itemSell: i.itemSell || 0,
        adultSell: i.adultSell || 0,
        childSell: i.childSell || 0,
        infantSell: i.infantSell || 0,
        currencyBuy: i.currencyBuy || "",
        currencySell: i.currencySell || "",
        customPrice: !!i.customPrice,
      };
    },
    fetchLive() {
      if (!this.item || !this.item.service || !this.item.service.id) return;
      if (!this.serviceStartDate || !this.serviceEndDate) return;
      this.loadingLive = true;
      this.liveError = "";
      this.$apollo
        .query({
          query: GET_PRICE_BREAKDOWN_QUERY,
          fetchPolicy: "network-only",
          variables: {
            serviceId: this.item.service.id,
            department: this.department || null,
            ratePlan: this.item.ratePlan || null,
            adults: Number(this.item.adults) || 0,
            children: Number(this.item.children) || 0,
            infants: 0,
            qty: Number(this.item.qty) || 1,
            startDate: this.serviceStartDate,
            endDate: this.serviceEndDate,
          },
        })
        .then(({ data }) => {
          this.loadingLive = false;
          const p = data && data.itemPricing;
          if (!p) {
            this.liveError = "No live pricing returned.";
            return;
          }
          if (p.message) this.liveError = p.message;
          this.form = {
            ...this.form,
            rateTypeCode: p.rateTypeCode || this.form.rateTypeCode,
            itemBuy: p.itemBuy || 0,
            adultBuy: p.adultBuy || 0,
            childBuy: p.childBuy || 0,
            infantBuy: p.infantBuy || 0,
            itemSell: p.itemSell || 0,
            adultSell: p.adultSell || 0,
            childSell: p.childSell || 0,
            infantSell: p.infantSell || 0,
            currencyBuy: p.currencyBuy || this.form.currencyBuy,
            currencySell: p.currencySell || this.form.currencySell,
          };
        })
        .catch((err) => {
          this.loadingLive = false;
          this.liveError = err.graphQLErrors
            ? err.graphQLErrors.map((e) => e.message).join(", ")
            : err.message;
        });
    },
    reloadLive() {
      this.fetchLive();
    },
    onRateTypeSelect(rateType) {
      this.rateType = rateType ? { ...rateType } : null;
    },
    onCancel() {
      this.$emit("close");
    },
    onSave() {
      if (!this.item) return;
      this.saving = true;
      const input = this.form.customPrice
        ? {
            customPrice: true,
            rateTypeCode: this.form.rateTypeCode,
            itemBuy: Number(this.form.itemBuy) || 0,
            adultBuy: Number(this.form.adultBuy) || 0,
            childBuy: Number(this.form.childBuy) || 0,
            infantBuy: Number(this.form.infantBuy) || 0,
            itemSell: Number(this.form.itemSell) || 0,
            adultSell: Number(this.form.adultSell) || 0,
            childSell: Number(this.form.childSell) || 0,
            infantSell: Number(this.form.infantSell) || 0,
            currencyBuy: this.form.currencyBuy,
            currencySell: this.form.currencySell,
          }
        : { customPrice: false };
      this.$apollo
        .mutate({
          mutation: UPDATE_OPEN_GROUP_DEPARTURE_ITEM,
          variables: {
            id: this.item.id,
            openGroupDepartureItemInput: input,
          },
        })
        .then(({ data }) => {
          this.saving = false;
          this.$emit("saved", data.updateOpenGroupDepartureItem);
          this.$emit("close");
        })
        .catch((err) => {
          this.saving = false;
          const msg = err.graphQLErrors
            ? err.graphQLErrors.map((e) => e.message).join(", ")
            : err.message;
          this.$store.dispatch("showAppSnackbar", { msg, color: "error" });
        });
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/gt/open-groups/builder/OpenGroupDepartureItemPricingDialog.vue